import React, { useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Button } from "../../../components/common/Button";
import { useForm } from "react-hook-form";
import { Types } from "../Types";
import { BusinessTitle, BusinessText, FormsFlex, ErrorText, PageHeaderWrapper, OverallContainer, OnboardingButtonContainer } from "../styles";
import { Input as AntInput } from "antd";
import BusinessLogoUpload from "../../../components/compliance/Steps/components/StepOne/BusinessLogoUpload";
import FormSection from "../components/FormSection";
import FormField from "../../../components/FormField/FormField";
import { TextAreaContainer } from "../../../components/compliance/Steps/components/StepOne/style";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { addBusinessInformation, getParticularStep } from "../redux/actions";
import Loader from "../../../components/common/Loader";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { useBusinessTypes } from "../../../app/hooks/useBusinessTypes";
import CustomSelect from "../../../components/common/CustomSelect";
import { useBusinessCategory } from "../../../app/hooks/useBusinessCategory";
import CountrySelect from "../../../components/common/CountrySelect";
import { updateBusinessType } from "../redux/slice";
import { defineBusinessTypes } from "../utils/defineBusinessTypes";
import { updateBusinessId } from "../../Login/usersSlice";
import { useWindowSize } from "@reach/window-size";
import { extractFileNameFromUrl } from "../utils/businessDocumentUtils";
import { enhancedGetCountryByAbbreviation } from "../../../utils/helpers";
import {CustomTextArea} from "../../../components/common/CustomTextArea";
import { GetMerchantBusinessLogo, saveEmailBusinessLogo } from "../../Settings/settingsAPI";

type Props = {
  handleNext: () => void;
};

const BusinessInfo = ({ handleNext }: Props) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const dispatch = useAppDispatch();
  const { businessTypes } = useBusinessTypes();
  const { businessCategories } = useBusinessCategory();
  const { businessId, userObj } = useAppSelector((state) => state.users);
  const { accountUser } = userObj;
  const { width } = useWindowSize()
  const { onShowAlert: onShowErrorAlert } =
    useContext(NetworkErrorAlertContext) || {};
  const { loading } = useAppSelector((state) => state.onboarding || {});

  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
    reset, 
  } = useForm<Types>({});

  const [hasSavedData, setHasSavedData] = useState(false);

  const handleAddBusinessInfo = async (data: Types) => {
    const businessData = {
      owner: accountUser.id,
      organizationType: data.businessType,
      id: data?.id,
      ...data,
    };

    const action = await dispatch(addBusinessInformation(businessData));

    if (addBusinessInformation.fulfilled.match(action)) {
      dispatch(updateBusinessId(action.payload.id));
      if (logoFile) {
        try {
          await saveEmailBusinessLogo(action.payload.id, logoFile);
        } catch (error) {
          onShowErrorAlert("", error?.response?.data?.message || "We could not upload your business logo. You can add it later in Settings.");
        }
      }
      dispatch(updateBusinessType(businessData?.businessType as any))
      handleNext();
    } else if (addBusinessInformation.rejected.match(action)) {
      onShowErrorAlert("", String(action.payload));
    }
  };

  const definedBusinessTypes = defineBusinessTypes(businessTypes)

  const [urlFromBE, setUrlFromBE] = useState(null)
  const getSavedCustomizationLogo = async () => {
    if (!businessId) return null
    try {
      const logoResponse = await GetMerchantBusinessLogo(businessId)
      return logoResponse?.data?.businessLogo
    } catch (error) {
      return null
    }
  }

  const getSavedBusinessInfo = async () => {
    const stepNumber = 1
    const action = await dispatch(getParticularStep({businessId, stepNumber}))
    
      if(getParticularStep.fulfilled.match(action)) {
        reset({
          businessType: action?.payload?.businessType,
          businessCategory: action?.payload?.businessCategory,
          address: action?.payload?.address,
          city: action?.payload?.city,
          state: action?.payload?.state,
          description: action?.payload?.description,
          id: businessId,
          registrationNumber: action?.payload?.registrationNumber,
        })
  
        setHasSavedData(true)
        const savedLogoUrl = action.payload.logoUrl || await getSavedCustomizationLogo()
        if(savedLogoUrl) {
          setUrlFromBE({name: extractFileNameFromUrl(savedLogoUrl) || "Business logo", uploaded: true})
        }
    
      } else if (getParticularStep.rejected.match(action)) {
        console.error('We could not retrieve previously saved data.');
      }

  }

useEffect(() => {
  getSavedBusinessInfo()
}, [])

return (
    <OverallContainer>
      <Loader isLoading={loading} />
      <PageHeaderWrapper>
        <BusinessTitle>Business Information</BusinessTitle>
        <BusinessText>Provide basic details about your business</BusinessText>
      </PageHeaderWrapper>
      <FormSection>
        <FormField
          name="businessName"
          control={control}
          label="Legal business name"
          defaultValue={accountUser?.businessName}
          required
          errors={errors}
        />
      </FormSection>

      <FormSection>
        <Controller
          name="businessType"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field }) => (
            <CustomSelect
              placeholder="Type of business"
              options={definedBusinessTypes}
              $height="48px"
              $width="100%"
              field={field}
              optionLabelProp="label"
              lightBorder={true}
              $showBorder={false}
              disabled={hasSavedData}
            />
          )}
        />
        {errors.businessType?.type === "required" && (
          <ErrorText>
            <span>Field is required</span>
          </ErrorText>
        )}
      </FormSection>

      <FormSection>
        <Controller
          name="businessCategory"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field }) => (
            <CustomSelect
              placeholder="Business sector"
              options={businessCategories}
              $height="48px"
              $width="100%"
              field={field}
              lightBorder={true}
            />
          )}
        />
        {errors.businessCategory?.type === "required" && (
          <ErrorText>
            <span>Field is required</span>
          </ErrorText>
        )}
      </FormSection>

      <FormSection>
        <FormField
          name="registrationNumber"
          control={control}
          label="RC number"
          required
          errors={errors}
        />
      </FormSection>

      <FormSection>
        <CountrySelect value={enhancedGetCountryByAbbreviation(accountUser?.businessCountry)} disabled  lightBorder={true} height="48px"/>
      </FormSection>

      <FormSection>
        <FormField
          name="address"
          control={control}
          label="Business address"
          required
          errors={errors}
        />
      </FormSection>

      <FormsFlex>
        <FormField
          name="city"
          control={control}
          label="City"
          required
          errors={errors}
        />
        <FormField
          name="state"
          control={control}
          label="State"
          required
          errors={errors}
        />
      </FormsFlex>

      <FormSection>
        <TextAreaContainer>
          <Controller
            name="description"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
               <CustomTextArea
                field={field}
                label="Business description"
                rows={5}
                maxLength={200}
              />
            )}
          />
          {errors.description && (
            <ErrorText>
              <span>Field is required</span>
            </ErrorText>
          )}
        </TextAreaContainer>
      </FormSection>

      <FormSection>
        <h1 className="business-logo-copy">
          Add your business logo (optional)
        </h1>
        <p className="business-logo-text">
          Your business logo will be included on all transaction receipts issued
          to your customers.
        </p>
        <BusinessLogoUpload
          onFileChange={setLogoFile}
          previouslySavedLogo={urlFromBE}
        />
      </FormSection>

      <OnboardingButtonContainer>
        <Button
          onClick={handleSubmit(handleAddBusinessInfo)}
          label="Continue"
          width={width < 1150 ? '' : '494px' }
          height="48px"
          fontSize="16px"
          disabled={!isValid || loading}
        />
      </OnboardingButtonContainer>
     
    </OverallContainer>
  );
};

export default BusinessInfo;
